import React, { useMemo } from 'react';
import { podFulFillmentUtils } from '@thd-olt-component-react/fulfillment';

export const useSelectedItems = ({
  products, loadedProductsData, loadingProgress, showSelectionScreen
}) => {

  const isLoading = loadingProgress !== 100;

  const selectedCatalogItems = useMemo(() => {
    if (!showSelectionScreen) {
      return null;
    }
    return products
      .filter((product) => (product.itemId && product.itemType === 'catalog') || product.itemType === 'intent')
      .map((item) => {
        if (item.itemType === 'intent') {
          return {
            intent: item.purchaseIntent,
            quantity: item.quantity,
            itemType: 'intent',
            groupName: item.groupName,
            comments: item.comments
          };
        }
        return {
          itemId: item.itemId,
          quantity: item.quantity,
          groupName: item.groupName,
          comments: item.comments
        };
      });
  }, [products]);

  const selectedQuotableCatalogItemDetails = useMemo(() => {
    if (isLoading) {
      return [];
    }

    const filteredCatalogProducts = products.filter((product) => product?.itemType === 'catalog');

    const eligibleProducts = filteredCatalogProducts.filter((filteredCatalogProduct) => {
      const productDetails = loadedProductsData.find((product) => product?.itemId === filteredCatalogProduct?.itemId);
      const { isATCEnabled } = podFulFillmentUtils;

      return productDetails?.identifiers?.productType !== 'MAJOR_APPLIANCE'
        && productDetails?.identifiers?.productType !== 'CONFIGURABLE_BLINDS'
        && !productDetails?.info?.isLiveGoodsProduct
        && (isATCEnabled && isATCEnabled(productDetails));
    });

    return eligibleProducts.map((filteredCatalogProduct) => {
      const productDetails = loadedProductsData.find((product) => product?.itemId === filteredCatalogProduct?.itemId);

      return {
        itemId: filteredCatalogProduct?.itemId,
        quantity: filteredCatalogProduct?.quantity,
        fulfillment: productDetails?.fulfillment,
        identifiers: productDetails?.identifiers,
        pricing: productDetails?.pricing
      };
    });
  }, [products, loadedProductsData, loadingProgress]);

  const selectedCatalogItemDetails = useMemo(() => {
    if (isLoading) {
      return [];
    }

    const filteredCatalogProducts = products.filter((product) => {
      return product?.itemType === 'catalog';
    });

    const getProductFlag = (productDetails) => {
      const { isATCEnabled } = podFulFillmentUtils;
      if (productDetails?.identifiers?.productType === 'MAJOR_APPLIANCE') {
        return 'MAJOR_APPLIANCE';
      }
      if (productDetails?.identifiers?.productType === 'CONFIGURABLE_BLINDS') {
        return 'CONFIGURABLE_BLINDS';
      }
      if (productDetails?.info?.isLiveGoodsProduct) {
        return 'LIVE_GOODS';
      }
      if (isATCEnabled && !isATCEnabled(productDetails)) {
        return 'DISCONTINUED_OOS_UNAVAILABLE';
      }
      return productDetails?.identifiers?.productType;
    };

    return filteredCatalogProducts.map((filteredCatalogProduct) => {
      const productDetails = loadedProductsData.find((product) => product?.itemId === filteredCatalogProduct.itemId);

      return {
        itemId: filteredCatalogProduct?.itemId,
        quantity: filteredCatalogProduct?.quantity,
        identifiers: productDetails?.identifiers,
        fulfillment: productDetails?.fulfillment,
        pricing: productDetails?.pricing,
        flag: getProductFlag(productDetails)
      };
    });
  }, [products, loadedProductsData, loadingProgress]);

  const selectedQCItemDetails = useMemo(() => {
    if (isLoading) {
      return [];
    }

    const filteredQCProducts = products.filter((product) => {
      return product?.itemType === 'qc-bridge-catalog';
    });

    return filteredQCProducts.map((filteredQCProduct) => {
      const productDetails = loadedProductsData.find((product) => product?.itemId === filteredQCProduct.itemId);

      return {
        id: filteredQCProduct?.id,
        itemId: filteredQCProduct?.itemId,
        quantity: filteredQCProduct?.quantity,
        identifiers: productDetails?.identifiers,
        fulfillment: productDetails?.fulfillment,
        flag: 'QC_PRODUCT'
      };
    });
  }, [products, loadedProductsData, loadingProgress]);

  const selectedNonSharedQCItemDetails = useMemo(() => {
    if (isLoading) {
      return [];
    }

    const filteredNonSharedQCProducts = products.filter((product) => {
      return product?.itemType === 'qc-bridge-catalog-non-shared';
    });

    return filteredNonSharedQCProducts.map((filteredNonSharedQCProduct) => {
      const productDetails = loadedProductsData.find((product) => product?.key === filteredNonSharedQCProduct?.id);

      return {
        id: filteredNonSharedQCProduct?.id,
        itemId: filteredNonSharedQCProduct?.itemId,
        quantity: filteredNonSharedQCProduct?.quantity,
        product: productDetails?.product,
        flag: 'NON_SHARED_QC_PRODUCT'
      };
    });
  }, [products, loadedProductsData, loadingProgress]);

  return {
    selectedCatalogItems,
    selectedQuotableCatalogItemDetails,
    selectedCatalogItemDetails,
    selectedQCItemDetails,
    selectedNonSharedQCItemDetails
  };
};